<template>
  <div class="product-form-page">
    <h1 class="ma-2 ml-0" v-if="!loading">{{ isUpdating ? "Editar" : "Cadastrar" }} Produto</h1>
    
    <v-card :loading="loading" class="mt-4">
      <div v-if="loading" class="d-flex justify-center pa-8">
        <v-progress-circular indeterminate color="secondary" size="64" />
      </div>

      <div v-show="!loading">
        <v-card-title class="py-4 d-flex align-center justify-space-between">
          <span class="text-h6">Informações do Produto</span>
          <v-btn icon @click="close">
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-tabs v-model="activeTab" background-color="transparent" color="primary" grow>
          <v-tab>
            <v-icon left>{{ icons.mdiPackageVariant }}</v-icon>
            Dados Gerais
          </v-tab>
          <v-tab :disabled="productForm.product_type == 2">
            <v-icon left>{{ icons.mdiScriptTextOutline }}</v-icon>
            Dados Fiscais
          </v-tab>
          <v-tab :disabled="productForm.product_type == 2">
            <v-icon left>{{ icons.mdiWarehouse }}</v-icon>
            Estoque
          </v-tab>
        </v-tabs>
        
        <v-divider></v-divider>

        <v-card-text class="pa-4">
          <v-form v-model="formValid" ref="form">
            <v-tabs-items v-model="activeTab">
              <!-- TAB 1: DADOS GERAIS -->
              <v-tab-item>
                <v-container fluid class="pa-0 mt-4">
                  <v-row class="mb-2">
                    <v-col cols="12" md="6">
                      <v-radio-group
                        v-model="productForm.product_type"
                        row
                        hide-details
                        class="mt-0"
                        :rules="[rules.required]"
                      >
                        <v-radio label="Produto" :value="1"></v-radio>
                        <v-radio label="Serviço" :value="2"></v-radio>
                      </v-radio-group>
                    </v-col>
                    <v-col cols="12" md="6" class="d-flex justify-md-end align-center">
                      <v-switch
                        v-model="productForm.has_integration"
                        inset
                        label="Habilitar Integração"
                        hide-details
                        class="mt-0"
                      ></v-switch>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="productForm.name"
                        :rules="[rules.required]"
                        label="Nome"
                        outlined
                        dense
                        hide-details="auto"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="productForm.sku"
                        :rules="[rules.required]"
                        label="Código (SKU)"
                        outlined
                        dense
                        hide-details="auto"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="productForm.bar_code"
                        :rules="productForm.product_type == 2 ? [] : [rules.required]"
                        :disabled="productForm.product_type == 2"
                        label="Código de Barras"
                        outlined
                        dense
                        hide-details="auto"
                      />
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="productForm.description"
                        :rules="[rules.required]"
                        label="Descrição"
                        outlined
                        dense
                        hide-details="auto"
                        rows="3"
                        auto-grow
                      />
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        v-model="productForm.unit"
                        label="Unidade"
                        outlined
                        dense
                        :rules="productForm.product_type == 2 ? [] : [rules.required]"
                        :disabled="productForm.product_type == 2"
                        hide-details="auto"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <v-text-field
                        v-model="productForm.weight"
                        label="Peso"
                        suffix="kg"
                        outlined
                        dense
                        :rules="productForm.product_type == 2 ? [] : [rules.required]"
                        :disabled="productForm.product_type == 2"
                        hide-details="auto"
                        type="number"
                      />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <money-input
                        ref="costInputRef"
                        label="Valor de Compra"
                        v-model="productForm.cost"
                        :rules="[rules.required]"
                      >
                      </money-input>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <money-input
                        ref="valueInputRef"
                        label="Valor de Venda"
                        v-model="productForm.value"
                        :rules="[rules.required]"
                      >
                      </money-input>
                    </v-col>
                  </v-row>
                  
                  <v-row v-if="productForm.product_type == 2" class="mt-4">
                     <v-col cols="12">
                       <v-subheader class="px-0 font-weight-bold">Serviço</v-subheader>
                       <v-divider class="mb-4"></v-divider>
                        <v-select
                          v-model="productForm.service_code"
                          :items="servicesCode"
                          label="Código de serviço"
                          dense
                          outlined
                          :rules="[rules.required]"
                          hide-details="auto"
                        ></v-select>
                     </v-col>
                  </v-row>

                </v-container>
              </v-tab-item>

              <!-- TAB 2: DADOS FISCAIS -->
              <v-tab-item v-if="productForm.product_type == 1">
                 <v-container fluid class="pa-0 mt-4">
                  <v-subheader class="px-0 font-weight-bold ml-1">Classificação</v-subheader>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="productForm.outgoing_cfop"
                        :items="cfops"
                        label="CFOP"
                        dense
                        outlined
                        :rules="[rules.required]"
                        hide-details="auto"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="productForm.ncm"
                        label="NCM"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[rules.onlyNumbers, rules.required]"
                        type="number"
                        maxlength="8"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        v-model="productForm.cest"
                        label="CEST"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[rules.onlyNumbers, rules.required]"
                        type="input"
                        maxlength="7"
                      />
                    </v-col>
                  </v-row>

                  <v-subheader class="px-0 font-weight-bold mt-4 ml-1">ICMS</v-subheader>
                  <v-divider ></v-divider>
                  <v-row class="mt-2">
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="productForm.origin"
                        :items="origins"
                        label="Código origem"
                        dense
                        outlined
                        :rules="[rules.required]"
                        hide-details="auto"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="productForm.cst_icms"
                        :items="cstsIcms"
                        label="CST ICMS"
                        dense
                        outlined
                        :rules="[rules.required]"
                        hide-details="auto"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-select
                        v-model="productForm.cst_icms_interstate"
                        :items="cstsIcms"
                        label="CST ICMS Interestadual"
                        dense
                        outlined
                        :rules="[rules.required]"
                        hide-details="auto"
                      ></v-select>
                    </v-col>
                    
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="productForm.icms_rate"
                        label="Alíquota ICMS"
                        suffix="%"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[rules.onlyNumbers, rules.required]"
                        type="number"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="productForm.calculation_base_reduction"
                        label="Red. Base Cálculo"
                        suffix="%"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[rules.onlyNumbers, rules.required]"
                        type="number"
                      />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="productForm.icms_st_rate"
                        label="Alíquota ICMS ST"
                        suffix="%"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[rules.onlyNumbers, rules.required]"
                        type="number"
                      />
                    </v-col>
                  </v-row>

                  <v-subheader class="px-0 font-weight-bold mt-4 ml-1">IPI</v-subheader>
                  <v-divider></v-divider>
                  <v-row class="mt-2">
                    <v-col cols="12" md="8">
                      <v-select
                        v-model="productForm.cst_ipi"
                        :items="cstsIpi"
                        label="CST IPI"
                        dense
                        outlined
                        :rules="[rules.required]"
                        hide-details="auto"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="productForm.ipi_rate"
                        label="Alíquota IPI"
                        suffix="%"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[rules.onlyNumbers, rules.required]"
                        type="number"
                      />
                    </v-col>
                  </v-row>

                  <v-subheader class="px-0 font-weight-bold mt-4 ml-1">PIS / COFINS</v-subheader>
                  <v-divider></v-divider>
                  <v-row class="mt-2">
                    <v-col cols="12" md="8">
                      <v-select
                        v-model="productForm.cst_pis"
                        :items="cstsFederal"
                        label="CST PIS"
                        dense
                        outlined
                        :rules="[rules.required]"
                        hide-details="auto"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="productForm.pis_rate"
                        label="Alíquota PIS"
                        suffix="%"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[rules.onlyNumbers, rules.required]"
                        type="number"
                      />
                    </v-col>
                    
                    <v-col cols="12" md="8">
                      <v-select
                        v-model="productForm.cst_cofins"
                        :items="cstsFederal"
                        label="CST COFINS"
                        :rules="[rules.required]"
                        dense
                        outlined
                        hide-details="auto"
                      ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="productForm.cofins_rate"
                        :rules="[rules.onlyNumbers, rules.required]"
                        label="Alíquota COFINS"
                        suffix="%"
                        outlined
                        dense
                        hide-details="auto"
                        type="number"
                        class="no-spinners"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-tab-item>

              <!-- TAB 3: ESTOQUE -->
              <v-tab-item v-if="productForm.product_type == 1">
                <v-container fluid class="pa-0 mt-4">
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="productForm.total_stock"
                        label="Estoque Atual"
                        outlined
                        dense
                        hide-details="auto"
                        type="number"
                      />
                    </v-col>
                  </v-row>
                  </v-row>
                  
                  <v-divider class="my-4"></v-divider>
                  <p class="text-subtitle-1 font-weight-bold mb-2">Detalhamento de Lotes</p>
                  
                  <v-data-table
                    :headers="stockHeaders"
                    :items="productForm.stock_details"
                    dense
                    class="elevation-1"
                    locale="pt-BR"
                    no-data-text="Nenhum lote encontrado."
                  >
                    <template v-slot:item.manufacturing_date="{ item }">
                      {{ formatDate(item.manufacturing_date) }}
                    </template>
                     <template v-slot:item.expiration_date="{ item }">
                      {{ formatDate(item.expiration_date) }}
                    </template>
                  </v-data-table>
                </v-container>
              </v-tab-item>
            </v-tabs-items>
          </v-form>
        </v-card-text>
          
        <v-divider></v-divider>
        
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="secondary" class="text-capitalize" text @click="close">
            Cancelar
          </v-btn>
          <v-btn class="text-capitalize white--text px-6" color="success" elevation="0" @click="submitForm">
            <v-icon left size="20">{{ icons.mdiContentSave }}</v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </div>
    </v-card>
  </div>
</template>

<script>
import { mdiContentSave, mdiShopping, mdiClose, mdiPackageVariant, mdiScriptTextOutline, mdiWarehouse } from "@mdi/js";
import {
  CST_ICMS,
  CST_PIS_COFINS,
  CST_IPI,
  CFOP_SAIDAS,
  ORIGIN_MERCHANDISE,
  SERVICE_CODE,
} from "@/constants/option";
import moneyInput from "@/components/app/ui/moneyInput.vue";
import formMixin from "@/util/mixins/formMixin";
import notification from "@/helper/notification";

export default {
  name: "ProductFormPage",

  components: {
    moneyInput,
  },

  mixins: [formMixin],

  data() {
    return {
      activeTab: 0,
      formValid: null,
      isUpdating: false,
      loading: false,
      icons: {
        mdiShopping,
        mdiContentSave,
        mdiClose,
        mdiPackageVariant,
        mdiScriptTextOutline,
        mdiWarehouse,
      },
      productForm: {
        id: null,
        product_type: null,
        name: null,
        bar_code: null,
        description: null,
        unit: null,
        weight: null,
        platform: null,
        calculation_base_reduction: null,
        icms_rate: null,
        icms_st_rate: null,
        product_platform_id: null,
        sku: null,
        link: null,
        cost: null,
        value: null,
        value: null,
        total_stock: 0,
        stock_details: [],
        status: 1,
        has_integration: false,
        outgoing_cfop: null,
        outgoing_cfop_description: null,
        ncm: null,
        cest: null,
        origin: 0,
        cst_ipi: null,
        cst_icms: null,
        cst_pis: null,
        pis_rate: null,
        ipi_rate: null,
        cofins_rate: null,
        cst_cofins: null,
        cst_icms_interstate: null,
        service_code: null,
      },
      cfops: CFOP_SAIDAS,
      cstsIcms: CST_ICMS,
      cstsFederal: CST_PIS_COFINS,
      cstsIpi: CST_IPI,
      cfopSelected: null,
      cstSelected: null,
      cstInterstateSelected: null,
      origins: ORIGIN_MERCHANDISE,
      servicesCode: SERVICE_CODE,
      stockHeaders: [
        { text: 'Lote', value: 'batch_number' },
        { text: 'Quantidade', value: 'quantity' },
        { text: 'Data de Fabricação', value: 'manufacturing_date' },
        { text: 'Validade', value: 'expiration_date' },
      ],
    };
  },
  mounted() {
    this.checkMode();
  },
  methods: {
    checkMode() {
      const id = this.$route.params.id;
      if (id) {
        this.isUpdating = true;
        this.productForm.id = id;
        this.getProduct(id);
      } else {
        this.isUpdating = false;
        // Reset form if needed or keep default values
      }
    },

    getProduct(id) {
      this.loading = true;
      this.$http.$get(`/product/${id}`, { params: { with: 'tax' }}).then((response) => {
        this.productForm = response.data;
        if (response.data.tax) {
          this.loadImposedData(response.data.tax)
        }
        if (this.$refs.valueInputRef) this.$refs.valueInputRef.reset(response.data.value);
        if (this.$refs.costInputRef) this.$refs.costInputRef.reset(response.data.cost);
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      }).catch(() => {
        this.loading = false;
        notification.showError("Erro ao carregar produto.");
      });
    },

    close() {
      this.$router.push({ name: 'products' });
    },

    submitForm() {
      if (this.$refs.form.validate()) {
        if (this.isUpdating) {
          this.$http
            .$put(`/product/${this.productForm.id}`, this.productForm)
            .then((response) => {
              if (response.status == 200) {
                notification.showSuccessMsg("Produto atualizado com sucesso!");
                this.close();
              }
            });
        } else {
          this.$http.$post("/product", this.productForm).then((response) => {
            if (response.status == 200) {
              notification.showSuccessMsg("Produto cadastrado com sucesso!");
              this.close();
            }
          });
        }
      } else {
        notification.showError("Por favor, verifique os campos obrigatórios em vermelho.");
        
        // Auto-switch tab to error
        this.$nextTick(() => {
             const tab1Fields = ['product_type', 'name', 'sku', 'bar_code', 'description', 'unit', 'weight', 'cost', 'value', 'service_code'];
             // Fiscal fields (Tab 2)
             const tab2Fields = ['outgoing_cfop', 'ncm', 'cest', 'origin', 'cst_icms', 'cst_icms_interstate', 'icms_rate', 'calculation_base_reduction', 'icms_st_rate', 'cst_ipi', 'ipi_rate', 'cst_pis', 'pis_rate', 'cst_cofins', 'cofins_rate'];
             // Stock fields (Tab 3)
             const tab3Fields = ['total_stock'];

             let invalidFields = [];
             // We can check the inputs ref if available, but checking data model is also possible if we knew exactly which failed. 
             // Better: Iterate over refs component inputs if possible.
             // Simple fallback: Check values manually for nulls based on required rules Logic.
             // OR: rely on Vuetify's internal error state if we can access it.
             
             // Let's iterate over children components of the form? No, too complex.
             // Let's check the productForm values against rules manually for simplicity and reliability.
             
             const isTab1Invalid = !this.productForm.name || !this.productForm.sku || !this.productForm.description || !this.productForm.value || !this.productForm.cost || (this.productForm.product_type == 1 && (!this.productForm.unit || !this.productForm.weight || !this.productForm.bar_code)) || (this.productForm.product_type == 2 && !this.productForm.service_code);
             
             if (isTab1Invalid) {
                 this.activeTab = 0;
                 return;
             }
             
             if (this.productForm.product_type == 1) {
                 const isTab2Invalid = !this.productForm.outgoing_cfop || !this.productForm.ncm || !this.productForm.cest || !this.productForm.cst_icms || !this.productForm.cst_icms_interstate || !this.productForm.icms_rate || !this.productForm.calculation_base_reduction || !this.productForm.icms_st_rate || !this.productForm.cst_ipi || !this.productForm.ipi_rate || !this.productForm.cst_pis || !this.productForm.pis_rate || !this.productForm.cst_cofins || !this.productForm.cofins_rate;
                 
                  if (isTab2Invalid) {
                     this.activeTab = 1;
                     return;
                 }
             }

        });
      }
    },
    loadImposedData(data) {
      this.productForm.origin = data.origin;
      this.productForm.cst_ipi = data.cst_ipi;
      this.productForm.cst_pis = data.cst_pis;
      this.productForm.pis_rate = data.pis_rate;
      this.productForm.cst_icms = data.cst_icms;
      this.productForm.ipi_rate = data.ipi_rate;
      this.productForm.icms_rate = data.icms_rate;
      this.productForm.cst_cofins = data.cst_cofins;
      this.productForm.cofins_rate = data.cofins_rate;
      this.productForm.icms_st_rate = data.icms_st_rate;
      this.productForm.outgoing_cfop = data.outgoing_cfop;
      this.productForm.cst_icms_interstate = data.cst_icms_interstate;
      this.productForm.outgoing_cfop_description = data.outgoing_cfop_description;
      this.productForm.calculation_base_reduction = data.calculation_base_reduction;
    },
    formatDate(date) {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('pt-BR');
    },
  },
  watch: {
    'productForm.outgoing_cfop'(newValue, oldValue) {
      if (newValue && newValue != oldValue) {
        var text = this.cfops.find(item => item.value === newValue)?.text;
        if (text) {
          this.productForm.outgoing_cfop_description = text.split(' - ')[1];
        }
      }
    },
  },
};
</script>

<style>
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] {
  -moz-appearance: textfield;
}
</style>
